/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { DataApiService } from './data-api-service';
import { RoutingService } from 'src/app/service/routing.service';
import { QueryApiService } from '../customer-api/query-api.service';
import { PortfolioApiService } from '../customer-api/portfolio-api.service';
import { TagUtils } from '../../utils/tag.utils';

/**
 * Shared routing logic for Data API requests.
 *
 * Replicates the relevant branches of yukkapi.iRequest() so that
 * domain services (TagcloudService, EventsService, SentimentService, etc.)
 * don't duplicate routing decisions.
 *
 * Branch order matches the old iRequest priority:
 *   0. Stats body (no type/id) → portfolio POST with body
 *   1. fromAlertsSetup → entity GET
 *   2. Scanner + tag → entity GET (resolved from tag)
 *   3. Folio/Scanner + scoring flags → entity GET or portfolio POST
 *   4. Market / portfolioId → entity GET, or portfolio POST (portfolio flag / marketPortfolio)
 *   5. Folio (default) → requestEntity GET or portfolio POST
 *   6. Query / Search → search_query POST (with resolveQuery/resolveQueryGroup for collections)
 *   7. Default → entity GET
 */
@Injectable({
  providedIn: 'root',
})
export class ContextualRequestService {
  constructor(
    private dataApiService: DataApiService,
    private routing: RoutingService,
    private queryApi: QueryApiService,
    private portfolioApi: PortfolioApiService,
  ) {}

  /**
   * Route a Data API request to the correct endpoint based on iparams flags
   * and the current routing context.
   */
  request(
    endpoint: string,
    iparams: any,
    params: any,
    version?: string,
  ): Observable<any> {
    // Stats context with body → POST portfolio with body payload
    if (iparams.body && !iparams.type && !iparams.id) {
      return this.dataApiService
        .post(`portfolio/${endpoint}`, iparams.body, params, version)
        .pipe(catchError(() => of(undefined)));
    }

    // fromAlertsSetup → always entity path
    if (iparams.fromAlertsSetup) {
      return this.dataApiService.get(
        `${iparams.type}/${iparams.id}/${endpoint}`,
        params,
        version,
      );
    }

    // Scanner with tag but no explicit type → resolve entity from tag
    if (
      this.routing.isScanner() &&
      iparams.tag &&
      !iparams.type &&
      !iparams.scoreForTabs &&
      !iparams.itemized
    ) {
      const parsedTag = TagUtils.parse(iparams.tag);
      if (parsedTag) {
        return this.dataApiService
          .get(`${parsedTag.type}/${parsedTag.id}/${endpoint}`, params, version)
          .pipe(catchError(() => of({})));
      }
    }

    // Folio/Scanner with portfolio scoring flags
    if (
      ((this.routing.isFolio() || this.routing.isScanner()) &&
        (iparams.scorePortfolio ||
          iparams.scoreForPortfolio ||
          iparams.scoreScanner)) ||
      iparams.usePortfolio
    ) {
      // scoreForPortfolio/usePortfolio → POST portfolio with body
      if (iparams.scoreForPortfolio || iparams.usePortfolio) {
        const body =
          iparams.custom_payload ?? this.portfolioApi.activePortfolioContent;
        return this.dataApiService.post(
          `portfolio/${endpoint}`,
          body,
          params,
          version,
        );
      }
      // scorePortfolio/scoreScanner → GET entity path
      return this.dataApiService
        .get(`${iparams.type}/${iparams.id}/${endpoint}`, params, version)
        .pipe(catchError(() => of({})));
    }

    // Market or portfolioId context
    if (this.routing.isMarket() || iparams.portfolioId) {
      // portfolio flag → POST with specific portfolio's content
      // (portFolios() ensures the list is loaded — deep links can hit this
      // before folioGuard has populated it; cached after first load)
      if (iparams.portfolio && iparams.portfolioId) {
        return this.portfolioApi.portFolios().pipe(
          catchError(() => of([])),
          switchMap((portfolios: any[]) => {
            const portfolio = portfolios?.find(
              (p: any) => p.uid === iparams.portfolioId,
            );
            if (portfolio) {
              return this.dataApiService
                .post(
                  `portfolio/${endpoint}`,
                  portfolio.content,
                  params,
                  version,
                )
                .pipe(catchError(() => of(undefined)));
            }
            return of({});
          }),
        );
      }
      // marketPortfolio → POST with folio content
      if (this.portfolioApi.activePortfolio?.marketPortfolio) {
        return this.dataApiService
          .post(
            `portfolio/${endpoint}`,
            this.portfolioApi.activePortfolioContent,
            params,
            version,
          )
          .pipe(catchError(() => of(undefined)));
      }
      // Default market → entity GET
      const type = iparams.type || 'all';
      const id = iparams.id || 'all';
      return this.dataApiService.get(
        `${type}/${id}/${endpoint}`,
        params,
        version,
      );
    }

    // Folio (non-scanner, non-scoring)
    if (this.routing.isFolio() && !iparams.portfolioId) {
      // requestEntity → entity path even in folio context
      if (iparams.requestEntity) {
        return this.dataApiService.get(
          `${iparams.type}/${iparams.id}/${endpoint}`,
          params,
          version,
        );
      }
      // Default folio → POST portfolio
      return this.dataApiService.post(
        `portfolio/${endpoint}`,
        this.portfolioApi.activePortfolioContent,
        params,
        version,
      );
    }

    // Query or Search → POST search_query
    if (this.routing.isQuery() || this.routing.isSearch()) {
      const url = `search_query/${endpoint}`;

      // newsfeedFromQuery uses a separate query context
      if (
        iparams.newsfeedFromQuery &&
        this.queryApi.queryForNewsfeed?.query?.query
      ) {
        return this.dataApiService
          .post(
            url,
            this.queryApi.queryForNewsfeed.query.query,
            params,
            version,
          )
          .pipe(catchError(() => of(undefined)));
      }

      // Search context → use raw query directly
      if (this.routing.isSearch()) {
        if (this.queryApi.query?.query) {
          return this.dataApiService
            .post(url, this.queryApi.query.query, params, version)
            .pipe(catchError(() => of(undefined)));
        }
        return of(undefined);
      }

      // Collection view (explicit flag) → resolve all queries in the group
      if (iparams.collection && iparams.id !== iparams.groupId) {
        if (this.queryApi.querys) {
          const collection = this.queryApi.querys.find(
            (c: any) => c.uid === iparams.groupId,
          );
          if (collection) {
            const payload = {
              operator: 'or',
              filters: [],
              groups: collection.content.map((item: any) => item.query),
            };
            return this.dataApiService
              .post(url, payload, params, version)
              .pipe(catchError(() => of(undefined)));
          }
        }
        return of(undefined);
      }

      // Single query within a collection → resolve the individual query
      if (this.queryApi.query && iparams.groupId !== iparams.id) {
        return this.queryApi
          .resolveQuery(this.queryApi.query.uid)
          .pipe(
            switchMap((response: any) =>
              this.dataApiService
                .post(url, response.query, params, version)
                .pipe(catchError(() => of(undefined))),
            ),
          );
      }

      // Collection/group view → resolve all queries in the group
      if (this.queryApi.query) {
        return this.queryApi.resolveQueryGroup(this.queryApi.query.uid).pipe(
          switchMap((response: any) => {
            const payload = {
              operator: 'or',
              filters: [],
              groups: response.content.map((item: any) => item.query),
            };
            return this.dataApiService
              .post(url, payload, params, version)
              .pipe(catchError(() => of(undefined)));
          }),
        );
      }

      return of(undefined);
    }

    // Entity context (default)
    const type = iparams.type || 'all';
    const id = iparams.id || 'all';
    return this.dataApiService.get(
      `${type}/${id}/${endpoint}`,
      params,
      version,
    );
  }
}
